import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  InfoOutlined,
  Translate,
  UnfoldLess,
  UnfoldMore,
} from "@material-ui/icons";
import clsx from "clsx";
import FlexBox from "view/components/Layout/FlexBox";
import Alert from "view/components/Alert";
import ConfirmModal from "view/components/ConfirmModal";
import AIAssistantIcon from "view/images/AIAssistantIcon";
import {
  OVERWRITE_TRANSLATION_CONFIRM_LABEL,
  OVERWRITE_TRANSLATION_CONFIRMATION,
  OVERWRITE_TRANSLATION_TITLE,
} from "tools/translation";
import {
  useHasLocalisationContext,
  useLocalisationContext,
} from "./LocalisationProvider";
import LocalisationFieldGroup from "./LocalisationFieldGroup";
import {LocalisationRichTextOptions} from "./LocalisationRichTextRow";
import useStyles from "./styles";

const DEFAULT_TOOLTIP =
  "Exhibitors see this text in their selected language. Anything left empty falls back to English (UK).";

type PendingTranslate = {
  targetKey: string;
  languageCode?: string;
};

type Props = {
  id: string;
  targetKeys: string[];
  title?: React.ReactNode;
  heading?: React.ReactNode;
  tooltip?: string;
  richTextOptions?: LocalisationRichTextOptions;
  hideFooterActions?: boolean;
  withTopMargin?: boolean;
  disableBottomMargin?: boolean;
  /**
   * Translations owned elsewhere (a Library master shown on a Show): every
   * row is locked and the panel has no actions (FS-2999 §4).
   */
  readOnly?: boolean;
  /** Footer action label; the Library panels read `Auto-fill` (FS-2999). */
  autoFillLabel?: string;
};

const LocalisationSectionInner = ({
  id,
  targetKeys,
  title,
  heading,
  tooltip = DEFAULT_TOOLTIP,
  richTextOptions,
  hideFooterActions,
  withTopMargin,
  disableBottomMargin,
  readOnly,
  autoFillLabel = "Auto-fill all",
}: Props) => {
  const classes = useStyles();
  const {
    controller,
    registerSection,
    override,
    reportExpanded,
    hasBar,
    scopeKeys,
  } = useLocalisationContext();

  const hasOwnControl = !hasBar;

  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState<PendingTranslate | null>(null);
  // Latch: once the user toggles anything themselves (a header, or the bar's
  // expand/collapse-all), later-arriving data must not fight their choice.
  const userAdjustedRef = useRef(false);
  const wasLoadedRef = useRef(false);
  const appliedOverrideNonceRef = useRef(0);

  const keys = useMemo(
    () => targetKeys.filter((key) => !!controller.getTarget(key)),
    [targetKeys, controller]
  );

  /**
   * Partially translated = expand. Judged over the provider's `scopeKeys`
   * when set (a subtask/grid modal is its own surface), otherwise over every
   * target of the controller.
   */
  const defaultExpanded = useMemo(() => {
    if (!scopeKeys) return controller.isPartiallyTranslated;

    const scoped = scopeKeys.filter((key) => !!controller.getTarget(key));

    return (
      scoped.some((key) => controller.hasAnyContent(key)) &&
      !scoped.every((key) => controller.isComplete(key))
    );
  }, [scopeKeys, controller]);

  useEffect(() => registerSection(id), [registerSection, id]);

  useEffect(() => {
    reportExpanded(id, expanded);
  }, [reportExpanded, id, expanded]);

  /**
   * Apply the default on every not-loaded → loaded transition: entity ids
   * arrive in waves (see `hasLoaded`), and a default applied between waves
   * would read still-empty values. Gate on `hasLoaded`, NOT `isLoading` —
   * this child's effects run before the host's fetch effect starts.
   */
  useEffect(() => {
    const isLoaded = controller.hasLanguages && controller.hasLoaded;
    const wasLoaded = wasLoadedRef.current;

    wasLoadedRef.current = isLoaded;

    if (!isLoaded || wasLoaded || userAdjustedRef.current) return;

    setExpanded(defaultExpanded);
  }, [controller.hasLoaded, controller.hasLanguages, defaultExpanded]);

  useEffect(() => {
    if (!override) return;
    if (override.nonce === appliedOverrideNonceRef.current) return;

    appliedOverrideNonceRef.current = override.nonce;
    userAdjustedRef.current = true;
    setExpanded(override.expanded);
  }, [override]);

  if (!controller.hasLanguages || !keys.length) return null;

  if (!hasOwnControl && !expanded) return null;

  const translatableKeys = keys.filter((key) => controller.canTranslate(key));
  const canAutoFillAll = translatableKeys.length > 0;
  // The footer actions span every language at once; with a single language
  // they only repeat what its row already offers.
  const hasMultipleLanguages = controller.languages.length > 1;
  const hasAnyContentInSection = keys.some((key) =>
    controller.hasAnyContent(key)
  );
  const isAutoFillingAll = keys.some((key) => controller.isBusy(key));

  const runTranslate = async ({targetKey, languageCode}: PendingTranslate) => {
    if (targetKey) {
      await controller.translate(targetKey, languageCode);
      return;
    }

    for (const key of translatableKeys) {
      // eslint-disable-next-line no-await-in-loop
      await controller.translate(key);
    }
  };

  const requestTranslate = (targetKey: string, languageCode?: string) => {
    const wouldOverwrite = languageCode
      ? controller.hasContent(targetKey, languageCode)
      : controller.hasAnyContent(targetKey);

    if (wouldOverwrite) {
      setPending({targetKey, languageCode});
      return;
    }

    runTranslate({targetKey, languageCode});
  };

  const requestTranslateAll = () => {
    if (hasAnyContentInSection) {
      setPending({targetKey: ""});
      return;
    }

    runTranslate({targetKey: ""});
  };

  const clearAll = () => keys.forEach((key) => controller.clear(key));

  return (
    <>
      {pending && (
        <ConfirmModal
          isCalledByAnotherModal
          className={classes.overwriteModal}
          title={OVERWRITE_TRANSLATION_TITLE}
          description={OVERWRITE_TRANSLATION_CONFIRMATION}
          submitTitle={OVERWRITE_TRANSLATION_CONFIRM_LABEL}
          onClose={() => setPending(null)}
          onSubmit={() => {
            const request = pending;
            setPending(null);
            runTranslate(request);
          }}
        />
      )}

      <Accordion
        classes={{
          root: clsx(
            classes.accordion,
            withTopMargin && classes.withTopMargin,
            disableBottomMargin && classes.noBottomMargin
          ),
        }}
        // Nothing mounts while collapsed — otherwise MUI keeps the body in the
        // DOM and a rich-text row spins up a Quill instance nobody can see.
        TransitionProps={{unmountOnExit: true}}
        expanded={hasOwnControl ? expanded : true}
        onChange={
          hasOwnControl
            ? (_, isExpanded) => {
                userAdjustedRef.current = true;
                setExpanded(isExpanded);
              }
            : undefined
        }
      >
        <AccordionSummary
          classes={{root: classes.summary, content: classes.summaryContent}}
          expandIcon={
            // eslint-disable-next-line no-nested-ternary
            hasOwnControl ? (
              expanded ? (
                <UnfoldLess color="primary" />
              ) : (
                <UnfoldMore color="primary" />
              )
            ) : null
          }
          style={hasOwnControl ? undefined : {cursor: "default"}}
          onClick={
            hasOwnControl ? undefined : (event) => event.preventDefault()
          }
          id={`localisation-summary-${id}`}
        >
          <FlexBox alignCenter gap={1}>
            {!title && (
              <Translate color="primary" className={classes.headerIcon} />
            )}
            <Typography variant="h3" color="primary">
              {title ?? "Localisation"}
            </Typography>
            {hasOwnControl && (
              <Tooltip title={tooltip}>
                <InfoOutlined className={classes.infoIcon} />
              </Tooltip>
            )}
          </FlexBox>
        </AccordionSummary>

        <AccordionDetails classes={{root: classes.details}}>
          {controller.loadFailed && (
            <Alert severity="warning">
              Existing translations could not be loaded, so any that are already
              saved are not shown here. Saving now only changes the languages
              you edit.
            </Alert>
          )}
          {heading && (
            <Typography variant="body2" className={classes.groupLabel}>
              Translation for <strong>{heading}</strong>
            </Typography>
          )}
          {keys.map((key) => (
            <LocalisationFieldGroup
              key={key}
              controller={controller}
              targetKey={key}
              onRequestTranslate={requestTranslate}
              richTextOptions={richTextOptions}
              readOnly={readOnly}
            />
          ))}

          {!hideFooterActions && !readOnly && hasMultipleLanguages && (
            <FlexBox
              alignCenter
              justifyContent="flex-end"
              className={classes.footer}
            >
              <Button
                color="primary"
                onClick={clearAll}
                disabled={!hasAnyContentInSection || isAutoFillingAll}
                id={`localisation-clear-all-${id}`}
              >
                Clear all
              </Button>
              <Button
                color="primary"
                variant="outlined"
                className={classes.autoFillButton}
                endIcon={
                  <AIAssistantIcon
                    disabled={!canAutoFillAll || isAutoFillingAll}
                  />
                }
                onClick={requestTranslateAll}
                disabled={!canAutoFillAll || isAutoFillingAll}
                id={`localisation-auto-fill-all-${id}`}
              >
                {autoFillLabel}
              </Button>
            </FlexBox>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

// Renders nothing outside a <LocalisationProvider> (e.g. Library masters,
// which are not translated per show) so shared editors can embed this freely.
const LocalisationSection = (props: Props) =>
  useHasLocalisationContext() ? <LocalisationSectionInner {...props} /> : null;

export default LocalisationSection;
